export type FocusedAlert = {
  _id: string;
  lat: number;
  lon: number;
  name: string;
  riskLevel: string;
};

type State = {
  /**
   * Turned on by a map that is currently drawing a route history for a user allowed to see
   * one (system admins). While it is off, focusing is a no-op - so no list row needs to know
   * about roles or about which map is on the page.
   */
  enabled: boolean;
  /** Alert under the cursor in a list; cleared as soon as the cursor leaves. */
  hovered: FocusedAlert | null;
  /** Alert clicked in the timeline; stays on the map until it is clicked again. */
  pinned: FocusedAlert | null;
};

type Listener = () => void;

const EMPTY_STATE: State = { enabled: false, hovered: null, pinned: null };

/**
 * Which alert the map should single out. While the route history overlay is on, the map
 * hides the markers for the whole period (they bury the path) and shows only what the user
 * points at in the timeline / alerts list. Kept outside React so a list item does not have
 * to be wired to the map through the generic ItemsList/Tabs plumbing.
 */
class AlertFocusStore {
  private state: State = EMPTY_STATE;

  private listeners = new Set<Listener>();

  getState = (): State => this.state;

  setEnabled = (enabled: boolean): void => {
    if (this.state.enabled === enabled) return;
    this.state = { enabled, hovered: null, pinned: null };
    this.emitChange();
  };

  setHovered = (alert: FocusedAlert | null): void => {
    if (!this.state.enabled || this.state.hovered?._id === alert?._id) return;
    this.state = { ...this.state, hovered: alert };
    this.emitChange();
  };

  /** Leaving a list item must not clear a marker another item has already taken over. */
  clearHovered = (alertId: string): void => {
    if (this.state.hovered?._id !== alertId) return;
    this.state = { ...this.state, hovered: null };
    this.emitChange();
  };

  togglePinned = (alert: FocusedAlert): void => {
    if (!this.state.enabled) return;
    const pinned = this.state.pinned?._id === alert._id ? null : alert;
    this.state = { ...this.state, pinned };
    this.emitChange();
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emitChange = (): void => {
    this.listeners.forEach((listener) => listener());
  };
}

const alertFocusStore = new AlertFocusStore();
export default alertFocusStore;
